import { auth } from "@/config/host.config";
import axios from "./auth-request-api";
import store from "@/store/index";
import { IAuth, ILogin } from "@/@types/auth/auth";
import { getPayload } from "@/services/accessDecoder";
import { AxiosResponse, AxiosError } from "axios";
import {
  prepareCredentials,
  fetchChallenge,
  resetKeyCache,
} from "./rsa-crypto";

export const AuthAPI = {
  login(
    login: string,
    password: string,
    nonce?: string | null
  ): Promise<AxiosResponse<IAuth>> {
    const url = auth.login;
    const data: { login: string; password: string; nonce?: string } = {
      login,
      password,
    };
    if (nonce) data.nonce = nonce;
    return axios.post(url, data);
  },
  logout(): Promise<AxiosResponse<IAuth>> {
    const url = auth.logout;
    return axios.post(url);
  },
};

const LOGIN_MAX_ATTEMPTS = 2;

function getErrorCode(error: AxiosError): string | undefined {
  const body = error.response?.data as { error?: string } | undefined;
  return body?.error;
}

export async function onLogin(loginData: ILogin): Promise<IAuth> {
  for (let attempt = 0; attempt < LOGIN_MAX_ATTEMPTS; attempt++) {
    const nonce = await fetchChallenge();
    const credentials = await prepareCredentials(
      loginData.login,
      loginData.password,
      nonce
    );

    try {
      const loginRes = await AuthAPI.login(
        credentials.login,
        credentials.password,
        nonce
      );
      if (loginRes.status === 200) {
        const access = loginRes.data.access;
        if (!access) {
          throw new Error("login response missing access token");
        }
        store.dispatch("setAccessToken", access);
        store.dispatch("setRefreshToken", loginRes.data.refreshtoken);
        const payload = getPayload(access);
        store.dispatch("setUserRoles", payload.rules);
        sessionStorage.setItem("login", loginData.login);
        return loginRes.data;
      }
    } catch (e) {
      const code = getErrorCode(e as AxiosError);
      const retryable =
        code === "nonce_invalid_or_used" ||
        code === "nonce_mismatch" ||
        code === "credentials_decrypt_error";
      if (attempt < LOGIN_MAX_ATTEMPTS - 1 && retryable) {
        if (code === "credentials_decrypt_error") {
          resetKeyCache();
        }
        continue;
      }
      throw e;
    }
  }
  throw new Error("login failed after retries");
}
